import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Truck, Plus, Trash, Check, X as XIcon, ArrowRight,
} from '@phosphor-icons/react';
import ReportToolbar from '../../../components/ReportToolbar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: 'bg-amber-50 text-amber-700 border-amber-300',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-300',
};

export default function StockTransfersTab() {
  const [transfers, setTransfers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
    lines: [{ item_description: '', quantity: 1, unit_cost: 0, bin_from: '', bin_to: '' }],
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, wRes, iRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/stock-transfers`),
        axios.get(`${API_URL}/inventory/warehouses`),
        axios.get(`${API_URL}/inventory`),
      ]);
      setTransfers(tRes.data);
      setWarehouses(wRes.data);
      setInventory(iRes.data);
    } catch (e) {
      toast.error('Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    const defaultWh = warehouses.find(w => w.is_default)?.id || (warehouses[0]?.id || '');
    const otherWh = warehouses.find(w => w.id !== defaultWh)?.id || '';
    setForm({
      from_warehouse_id: defaultWh,
      to_warehouse_id: otherWh,
      date: new Date().toISOString().slice(0, 10),
      notes: '',
      lines: [{ item_description: '', quantity: 1, unit_cost: 0, bin_from: '', bin_to: '' }],
    });
    setShowForm(true);
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { item_description: '', quantity: 1, unit_cost: 0, bin_from: '', bin_to: '' }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, key, val) => setForm(f => ({
    ...f,
    lines: f.lines.map((ln, i) => i === idx ? { ...ln, [key]: val } : ln),
  }));

  const save = async () => {
    if (!form.from_warehouse_id || !form.to_warehouse_id || form.from_warehouse_id === form.to_warehouse_id) {
      toast.error('Pick different From and To warehouses');
      return;
    }
    const validLines = form.lines.filter(l => l.item_description && Number(l.quantity) > 0);
    if (validLines.length === 0) {
      toast.error('Add at least one item with a positive quantity');
      return;
    }
    try {
      await axios.post(`${API_URL}/inventory/stock-transfers`, {
        from_warehouse_id: form.from_warehouse_id,
        to_warehouse_id: form.to_warehouse_id,
        date: form.date,
        notes: form.notes || null,
        lines: validLines.map(l => ({
          item_description: l.item_description,
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost) || 0,
          bin_from: l.bin_from || null,
          bin_to: l.bin_to || null,
        })),
      });
      toast.success('Transfer created as draft');
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const approve = async (t) => {
    try {
      await axios.post(`${API_URL}/inventory/stock-transfers/${t.id}/approve`);
      toast.success(`Transfer ${t.transfer_number} approved — stock posted`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const cancel = async (t) => {
    if (!window.confirm(`Cancel draft transfer ${t.transfer_number}?`)) return;
    try {
      await axios.post(`${API_URL}/inventory/stock-transfers/${t.id}/cancel`);
      toast.success('Transfer cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <Card className="p-4" data-testid="stock-transfers-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Truck size={20} weight="duotone" />
            Stock Transfers (TRF-####)
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Move stock between warehouses. Drafts post movements when approved.
          </p>
        </div>
        <Button onClick={openNew} disabled={warehouses.length < 2} data-testid="new-transfer-btn">
          <Plus size={16} className="mr-2" /> New Transfer
        </Button>
      </div>

      {warehouses.length < 2 && (
        <div className="mb-4 p-3 rounded-md bg-amber-50 text-amber-900 border border-amber-200 text-sm">
          You need at least <strong>two warehouses</strong> to create a stock transfer. Add another warehouse on the Warehouses tab first.
        </div>
      )}

      <ReportToolbar
        testidPrefix="transfers-toolbar"
        downloadBaseName="stock-transfers"
        exportUrl={(fmt) => `${API_URL}/reports/stock-transfers.${fmt}`}
      />

      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Loading…</div>
      ) : transfers.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No transfers yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">TRF #</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Date</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">From → To</th>
                <th className="text-right px-3 py-2 font-semibold uppercase text-xs tracking-wide">Lines</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Status</th>
                <th className="text-left px-3 py-2 font-semibold uppercase text-xs tracking-wide">Created by</th>
                <th className="text-right px-3 py-2 font-semibold uppercase text-xs tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {transfers.map(t => (
                <tr key={t.id} className="hover:bg-muted/30" data-testid={`transfer-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono">{t.transfer_number}</td>
                  <td className="px-3 py-2">{t.date}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="font-medium">{t.from_warehouse_name}</span>
                      <ArrowRight size={12} className="text-muted-foreground" />
                      <span className="font-medium">{t.to_warehouse_name}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{(t.lines || []).length}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={STATUS_BADGE[t.status] || ''}>{t.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{t.created_by_name}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    {t.status === 'draft' && (
                      <>
                        <Button size="sm" variant="default" onClick={() => approve(t)} data-testid={`approve-transfer-${t.id}`}>
                          <Check size={14} className="mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => cancel(t)} data-testid={`cancel-transfer-${t.id}`}>
                          <XIcon size={14} />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl" data-testid="transfer-form">
          <DialogHeader>
            <DialogTitle>New Stock Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>From warehouse *</Label>
                <Select value={form.from_warehouse_id} onValueChange={v => setForm({ ...form, from_warehouse_id: v })}>
                  <SelectTrigger data-testid="trf-from"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To warehouse *</Label>
                <Select value={form.to_warehouse_id} onValueChange={v => setForm({ ...form, to_warehouse_id: v })}>
                  <SelectTrigger data-testid="trf-to"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Line items</Label>
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs">
                    <tr>
                      <th className="text-left px-2 py-1.5">Item</th>
                      <th className="text-right px-2 py-1.5 w-24">Qty</th>
                      <th className="text-right px-2 py-1.5 w-28">Unit Cost</th>
                      <th className="text-left px-2 py-1.5 w-28">Bin from</th>
                      <th className="text-left px-2 py-1.5 w-28">Bin to</th>
                      <th className="px-2 py-1.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {form.lines.map((ln, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-1.5">
                          <Input list={`inv-items-${idx}`} value={ln.item_description}
                                 onChange={e => updateLine(idx, 'item_description', e.target.value)}
                                 placeholder="Item description"
                                 data-testid={`trf-line-item-${idx}`} />
                          <datalist id={`inv-items-${idx}`}>
                            {inventory.map(it => <option key={it.id} value={it.description} />)}
                          </datalist>
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" min="0" step="any" value={ln.quantity}
                                 onChange={e => updateLine(idx, 'quantity', e.target.value)}
                                 data-testid={`trf-line-qty-${idx}`} />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" min="0" step="any" value={ln.unit_cost}
                                 onChange={e => updateLine(idx, 'unit_cost', e.target.value)} />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={ln.bin_from} onChange={e => updateLine(idx, 'bin_from', e.target.value)}
                                 placeholder="(opt)" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input value={ln.bin_to} onChange={e => updateLine(idx, 'bin_to', e.target.value)}
                                 placeholder="(opt)" />
                        </td>
                        <td className="px-2 py-1.5">
                          {form.lines.length > 1 && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeLine(idx)}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button size="sm" variant="outline" className="mt-2" onClick={addLine} data-testid="trf-add-line">
                <Plus size={14} className="mr-1" /> Add line
              </Button>
            </div>

            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Reason / reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="trf-save">Save as Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
